/**
 * dosageLabel.js
 * ---------------
 * Small utility that pulls a "quantity + unit" tag out of a product
 * title. Supports mg / ml / mcg / iu / g out of the box — those are
 * the units used across compound-pharma / peptide research
 * catalogs, and mirror what the admin panel accepts in the
 * dedicated `dosage` field for legacy products.
 *
 * Regex breakdown: `(\d+(?:\.\d+)?)\s*(mg|ml|mcg|iu|g)\b`
 *   • `\d+(?:\.\d+)?`  — integer or decimal (e.g. `84`, `10.5`).
 *   • `\s*`            — optional space between number and unit
 *                        (so "84 mg" and "84mg" both work).
 *   • `(mg|ml|mcg|iu|g)` — the unit set. Add new units here if the
 *                        catalog ever ships them.
 *   • `\b`             — word boundary so "84mgs" or "84mgxy" don't
 *                        match a partial unit that would render badly.
 *
 * Case-insensitive so admins entering "84MG" or "10 ML" still work.
 * Returns the FIRST match — a title like
 * "Retatrutide 84mg Bundle (2x)" resolves to `{value:'84', unit:'mg'}`.
 */

const DOSAGE_LABEL_RE = /(\d+(?:\.\d+)?)\s*(mg|ml|mcg|iu|g)\b/i;

/**
 * Extract the {value, unit} tuple from a product title, or null if
 * no supported unit appears. Consumers should treat `null` as "no
 * badge for this product" (bundles, apparel, etc. that don't carry
 * a dose signature).
 */
export function extractDosageLabel(title) {
  if (!title || typeof title !== "string") return null;
  const m = title.match(DOSAGE_LABEL_RE);
  if (!m) return null;
  return {
    value: m[1],
    unit: m[2].toLowerCase(),
  };
}

/**
 * Format a `{value, unit}` tuple for display. Kept as a separate
 * helper so both the badge and any future comparison logic
 * (e.g. sorting products by strength) share one source of truth.
 * Preserves the value verbatim so "10" stays "10" and "10.5" stays
 * "10.5" — no rounding.
 */
export function formatDosageLabel(parts) {
  if (!parts) return "";
  return `${parts.value}${parts.unit}`;
}
